import { roleName } from "constants_map";
import { useAuthContext } from "context/AuthContext";
import { t } from "i18next";

/**
 * Custom hook to provide user configuration and role-based utilities.
 *
 * @returns {{
 *   isEA: boolean,
 *   isEU: boolean,
 *   isPA: boolean,
 *   isGU: boolean,
 *   isESU: boolean,
 *   loggedInRole: string,
 *   userNameConfig: {
 *     schema: function(Object): Object,
 *     condition: boolean
 *   }
 * }}
 */
function useUsersConfig() {
  const { globalConfig, userRole } = useAuthContext();

  /**
   * Flag indicating whether alphanumeric and special character validation is enabled for usernames.
   * @type {string}
   */
  const isUserAlphaNumSpclCharEnabled =
    globalConfig?.find((config) => config?.key === "userAlphaNumSpclChar")
      ?.value || "false";

  /**
   * Regular expression to validate usernames.
   * Must start with an alphanumeric character and can include @, ., &, space, hyphen, and underscore.
   * @type {RegExp}
   */
  const REGEX_ALPHANUMERIC_SPECIAL_CHARS = /^[a-zA-Z0-9][a-zA-Z0-9@.&\s_-]*$/;

  /**
   * Mapping of user roles to short codes.
   * @type {Object<string, string>}
   */
  const loggedInRoleMap = {
    [roleName.enterpriseAdmin]: "EA",
    [roleName.enterpriseUser]: "EU",
    [roleName.platformAdmin]: "PA",
    [roleName.globalUser]: "GU",
    [roleName.enterpriseSuperUser]: "ESU",
    [roleName.Finance]: "PFA",
    [roleName.Marketing]: "PMA",
  };

  /**
   * Short code for the currently logged-in user's role.
   * @type {string}
   */
  const loggedInRole = loggedInRoleMap[userRole] || "NA";

  return {
    /** @type {boolean} Whether the user is an Enterprise Admin */
    isEA: userRole === roleName.enterpriseAdmin,

    /** @type {boolean} Whether the user is an Enterprise User */
    isEU: userRole === roleName.enterpriseUser,

    /** @type {boolean} Whether the user is a Platform Admin */
    isPA: userRole === roleName.platformAdmin,

    /** @type {boolean} Whether the user is a Global User */
    isGU: userRole === roleName.globalUser,

    /** @type {boolean} Whether the user is an Enterprise Super User */
    isESU: userRole === roleName.enterpriseSuperUser,

    /** @type {string} Short code for the logged-in user's role */
    loggedInRole,

    /** @type {{schema: function(Object): Object, condition: boolean}} Configuration for validating usernames */
    userNameConfig: {
      /**
       * Adds regex-based validation to a schema.
       * @param {Object} schema - The validation schema object.
       * @returns {Object} The updated schema with regex match condition.
       */
      schema: (schema) =>
        schema.matches(
          REGEX_ALPHANUMERIC_SPECIAL_CHARS,
          t("_Only_alphanumeric_start_and_@._&_hyphen_underscore_space_allowed")
        ),

      /** @type {boolean} Whether the username validation condition is enabled */
      condition: isUserAlphaNumSpclCharEnabled === "true",
    },
  };
}

export default useUsersConfig;
